import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Autocomplete,
  Button,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import CustomModal from "../../../../common-utilities/core/modal";
import { HTTP_METHODS, invokeApi } from "../../../utils/http-service";
import { FINANCE_V2_HOSTNAME } from "../../../../common-utilities/utils/end-points";
import { FINANCE_REST_URLS } from "../../../../common-utilities/utils/end-points/finance-v2";
import FinancialYearDropdown from "../../home/financial-year-dropdown";

const PR_TYPES = [
  { label: "OPEX", value: "OPEX" },
  { label: "CAPEX", value: "CAPEX" },
];

const formatCurrency = (amount) =>
  typeof amount === "number"
    ? `₹${amount.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`
    : "—";

const getSiteLabel = (op) =>
  op ? [op.code, op.name].filter(Boolean).join(" - ") : "";

const PRCreateModal = ({ onClose }) => {
  const navigate = useNavigate();

  // Core form selections
  const [financialYear, setFinancialYear] = useState(null);
  const [prType, setPrType] = useState(null);
  const [site, setSite] = useState(null);
  const [selectedLineItems, setSelectedLineItems] = useState([]);

  // Data loaded from API
  const [allSites, setAllSites] = useState([]);
  const [budgetLineItems, setBudgetLineItems] = useState([]);
  const [activeBudgetId, setActiveBudgetId] = useState(null);

  // HO aggregation helpers
  const [headOfficeLineItemMap, setHeadOfficeLineItemMap] = useState({});
  const [validBudgetIds, setValidBudgetIds] = useState([]);

  // Per-line-item availability: { [lineItemId]: { checking, totalAllocated, reserved, freeBalance } }
  const [budgetAvailabilityMap, setBudgetAvailabilityMap] = useState({});

  // ── site loader ──────────────────────────────────────────────────────────────

  const loadSites = (prTypeValue, fy) => {
    const poType = prTypeValue === "OPEX" ? "opex_po" : "capex_po";

    invokeApi(
      HTTP_METHODS.GET,
      `${FINANCE_V2_HOSTNAME}${FINANCE_REST_URLS.LOCATIONS}/list`,
      null,
      { poType, budgetStatus: "approved", financialYear: fy, additionalFields: "locationType" }
    )
      .then((response) => {
        if (!response.message) {
          const sites = Array.isArray(response) ? response : response.results || [];
          setAllSites(sites);
        }
      })
      .catch(console.log);
  };

  // ── budget loaders ────────────────────────────────────────────────────────────

  const clearBudgets = () => {
    setBudgetLineItems([]);
    setSelectedLineItems([]);
    setBudgetAvailabilityMap({});
    setActiveBudgetId(null);
    setHeadOfficeLineItemMap({});
    setValidBudgetIds([]);
  };

  const loadBudgetsForStore = (siteId, prTypeValue, fy) => {
    clearBudgets();
    const budgetType = prTypeValue === "OPEX" ? "opex_budget" : "capex_budget";
    invokeApi(
      HTTP_METHODS.GET,
      `${FINANCE_V2_HOSTNAME}${FINANCE_REST_URLS.GET_BUDGETS}`,
      null,
      {
        page: 1,
        status: "approved",
        site: siteId,
        limit: 1000,
        budgetType,
        dropdown: true,
        financialYear: fy,
      }
    )
      .then((response) => {
        if (!response.message && response.totalResults > 0) {
          const budget = response.results[0];
          setActiveBudgetId(budget.id ?? budget._id);
          setBudgetLineItems(
            (budget.lineItems || []).filter((l) => l.status === "approved")
          );
        }
      })
      .catch(console.log);
  };

  const loadBudgetsForHeadOffice = (prTypeValue, fy, hoSiteId) => {
    clearBudgets();
    if (!hoSiteId) return;

    const budgetType = prTypeValue === "OPEX" ? "opex_budget" : "capex_budget";

    invokeApi(
      HTTP_METHODS.GET,
      `${FINANCE_V2_HOSTNAME}${FINANCE_REST_URLS.GET_BUDGETS}`,
      null,
      {
        page: 1,
        status: "approved",
        site: hoSiteId,
        limit: 1000,
        budgetType,
        dropdown: true,
        financialYear: fy,
      }
    )
      .then((response) => {
        if (!response.message && response.totalResults > 0) {
          const budget = response.results[0];
          const budgetId = budget.id ?? budget._id;
          const lineItems = (budget.lineItems || []).filter(
            (l) => l.status === "approved"
          );

          const catMap = {};
          const uniqueLineItems = [];
          lineItems.forEach((item) => {
            const catId = String(
              item.category?._id ?? item.category?.id ?? item.category ?? ""
            );
            const lineItemId = item._id ?? item.id;
            if (!catId || lineItemId == null) return;
            if (!catMap[catId]) {
              catMap[catId] = [];
              uniqueLineItems.push(item);
            }
            catMap[catId].push(lineItemId);
          });

          setActiveBudgetId(budgetId);
          setHeadOfficeLineItemMap(catMap);
          setValidBudgetIds(budgetId != null ? [budgetId] : []);
          setBudgetLineItems(uniqueLineItems);
        }
      })
      .catch(console.log);
  };

  // ── budget availability ───────────────────────────────────────────────────────

  const fetchAvailability = (lineItem, currentSite, currentFY, hoMap, hoBudgetIds) => {
    const lineItemId = String(lineItem._id ?? lineItem.id ?? "");
    if (!lineItemId) return;

    const setChecking = () =>
      setBudgetAvailabilityMap((prev) => ({
        ...prev,
        [lineItemId]: { checking: true },
      }));

    const setResult = (data) =>
      setBudgetAvailabilityMap((prev) => ({
        ...prev,
        [lineItemId]: { checking: false, ...data },
      }));

    const setEmpty = () =>
      setResult({ totalAllocated: 0, reservedAmount: 0, availableBudgetCost: 0 });

    const scope =
      currentSite?.locationType === "HO" ? "HEAD_OFFICE" : "SINGLE_STORE";

    if (scope === "HEAD_OFFICE") {
      const catId = String(
        lineItem.category?._id ?? lineItem.category?.id ?? lineItem.category ?? ""
      );
      const idsForCat = hoMap[catId] || [];
      if (idsForCat.length === 0) {
        setEmpty();
        return;
      }

      setChecking();
      const params = { lineItemIds: idsForCat.join(",") };
      if (hoBudgetIds.length > 0) params.budgetIds = hoBudgetIds.join(",");

      invokeApi(
        HTTP_METHODS.GET,
        `${FINANCE_V2_HOSTNAME}${FINANCE_REST_URLS.GET_BUDGET_AVAILABILITY}`,
        null,
        params
      )
        .then((res) => {
          if (!res.message && res.items) {
            let totalAllocated = 0,
              reservedAmount = 0,
              availableBudgetCost = 0;
            res.items.forEach((i) => {
              totalAllocated += i.totalAllocated || 0;
              reservedAmount += i.reservedAmount || 0;
              availableBudgetCost += i.availableBudgetCost || 0;
            });
            setResult({ totalAllocated, reservedAmount, availableBudgetCost });
          } else {
            setEmpty();
          }
        })
        .catch(setEmpty);
      return;
    }

    // SINGLE_STORE
    const storeId = currentSite?.id ?? currentSite?._id;
    if (!storeId || !currentFY) return;

    setChecking();
    invokeApi(
      HTTP_METHODS.GET,
      `${FINANCE_V2_HOSTNAME}${FINANCE_REST_URLS.GET_BUDGET_AVAILABILITY}`,
      null,
      { budgetHead: lineItemId, storeId, financialYear: currentFY }
    )
      .then((res) => {
        if (!res.message) {
          setResult({
            totalAllocated: res.totalAllocated || 0,
            reservedAmount: res.reservedAmount || 0,
            availableBudgetCost: res.availableBudgetCost || 0,
          });
        } else {
          setEmpty();
        }
      })
      .catch(setEmpty);
  };

  const onLineItemsChange = (newItems) => {
    const newIdSet = new Set(newItems.map((i) => String(i._id ?? i.id ?? "")));

    // Fetch availability for newly added items
    newItems.forEach((item) => {
      const id = String(item._id ?? item.id ?? "");
      if (!budgetAvailabilityMap[id]) {
        fetchAvailability(
          item,
          site,
          financialYear,
          headOfficeLineItemMap,
          validBudgetIds
        );
      }
    });

    // Prune removed items from map
    setBudgetAvailabilityMap((prev) => {
      const next = {};
      Object.entries(prev).forEach(([k, v]) => {
        if (newIdSet.has(k)) next[k] = v;
      });
      return next;
    });

    setSelectedLineItems(newItems);
  };

  // ── guards & navigation ───────────────────────────────────────────────────────

  const isCreateDisabled = () => {
    if (!financialYear || !prType?.value || !site || !activeBudgetId) return true;
    if (selectedLineItems.length === 0) return true;

    const anyChecking = selectedLineItems.some(
      (l) => budgetAvailabilityMap[String(l._id ?? l.id ?? "")]?.checking
    );
    if (anyChecking) return true;

    const hasSufficient = selectedLineItems.some((l) => {
      const avail = budgetAvailabilityMap[String(l._id ?? l.id ?? "")];
      return avail && (avail.availableBudgetCost || 0) > 0;
    });
    return !hasSufficient;
  };

  const onCreate = () => {
    if (!activeBudgetId || selectedLineItems.length === 0) return;
    const lineItemIds = selectedLineItems
      .map((l) => l._id ?? l.id)
      .filter(Boolean)
      .join(",");

    const siteId = site.id ?? site._id;

    const derivedPrScope = site?.locationType === 'STORE' ? 'SINGLE_STORE' : 'HEAD_OFFICE';

    const url =
      `purchase-requisition/create/${activeBudgetId}` +
      `?prType=${prType.value}` +
      `&lineItemIds=${lineItemIds}` +
      `&prScope=${derivedPrScope}` +
      `&financialYear=${financialYear}` +
      `&siteId=${siteId}`;

    navigate(url);
  };

  const handleClose = () => {
    setFinancialYear(null);
    setPrType(null);
    setSite(null);
    setSelectedLineItems([]);
    setAllSites([]);
    clearBudgets();
    onClose();
  };

  // ── FY wrapper (intercept FinancialYearDropdown's setFilters calls) ───────────
  // FinancialYearDropdown expects { filters, setFilters } where filters.financialYear is the value.
  const fyFilters = { financialYear };
  const setFyFilters = (updater) => {
    const next =
      typeof updater === "function" ? updater(fyFilters) : updater;
    const newFY = next?.financialYear ?? null;
    if (newFY !== financialYear) {
      setFinancialYear(newFY);
      // Changing FY resets everything downstream
      setPrType(null);
      setSite(null);
      setAllSites([]);
      clearBudgets();
    }
  };

  // ── handlers ─────────────────────────────────────────────────────────────────

  const handlePrTypeChange = (val) => {
    setPrType(val);
    setSite(null);
    setAllSites([]);
    clearBudgets();
    if (val?.value && financialYear) {
      loadSites(val.value, financialYear);
    }
  };

  const handleSiteChange = (val) => {
    setSite(val);
    clearBudgets();
    if (!val || !prType?.value || !financialYear) return;

    if (val.locationType === "HO") {
      loadBudgetsForHeadOffice(prType.value, financialYear, val.id ?? val._id);
    } else {
      loadBudgetsForStore(val.id ?? val._id, prType.value, financialYear);
    }
  };

  // ── render ────────────────────────────────────────────────────────────────────

  return (
    <CustomModal
      title="Create Purchase Requisition"
      disabledOutsideClick={true}
      onClose={handleClose}
    >
      <div className="finance-v2-po-modal-content">
        {/* Step 1: Financial Year */}
        <FinancialYearDropdown
          filters={fyFilters}
          setFilters={setFyFilters}
          isFullWidth={true}
        />

        {/* Step 2: PR Type */}
        <Autocomplete
          value={prType}
          onChange={(_, val) => handlePrTypeChange(val)}
          renderInput={(params) => (
            <TextField {...params} size="small" label="PR Type *" />
          )}
          options={PR_TYPES}
          isOptionEqualToValue={(opt, val) => opt.value === val?.value}
        />

        {/* Step 3: Site — filtered by FY + PR type via approved budgets */}
        <Autocomplete
          value={site}
          onChange={(_, val) => handleSiteChange(val)}
          getOptionLabel={getSiteLabel}
          isOptionEqualToValue={(opt, val) => (opt.id ?? opt._id) === (val?.id ?? val?._id)}
          renderInput={(params) => (
            <TextField {...params} size="small" label="Site *" />
          )}
          options={allSites}
          disabled={!prType?.value || !financialYear}
        />

        {/* Step 4: Budget Category */}
        <Autocomplete
          multiple
          value={selectedLineItems}
          onChange={(_, val) => onLineItemsChange(val || [])}
          getOptionLabel={(op) => op.name || ""}
          isOptionEqualToValue={(opt, val) =>
            (opt._id ?? opt.id) === (val?._id ?? val?.id)
          }
          renderInput={(params) => (
            <TextField {...params} size="small" label="Budget Category *" />
          )}
          options={budgetLineItems}
          disabled={!site}
        />

        {/* Budget availability per selected category */}
        {selectedLineItems.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {selectedLineItems.map((item) => {
              const id = String(item._id ?? item.id ?? "");
              const avail = budgetAvailabilityMap[id];
              if (!avail) return null;
              const insufficient =
                !avail.checking && (avail.availableBudgetCost || 0) <= 0;
              return (
                <div
                  key={id}
                  style={{
                    padding: "6px 10px",
                    borderRadius: 4,
                    backgroundColor: insufficient ? "#fff5f5" : "#f5faf5",
                    border: `1px solid ${insufficient ? "#ffcdd2" : "#c8e6c9"}`,
                  }}
                >
                  <Typography
                    variant="caption"
                    display="block"
                    fontWeight={600}
                    gutterBottom
                  >
                    {item.name}
                  </Typography>
                  {avail.checking ? (
                    <CircularProgress size={12} />
                  ) : (
                    <div
                      style={{
                        display: "flex",
                        gap: 16,
                        fontSize: 11,
                        flexWrap: "wrap",
                      }}
                    >
                      <span>
                        Allocated:{" "}
                        <strong>{formatCurrency(avail.totalAllocated)}</strong>
                      </span>
                      <span>
                        Reserved:{" "}
                        <strong>{formatCurrency(avail.reservedAmount)}</strong>
                      </span>
                      <span
                        style={{
                          color: insufficient ? "#c62828" : "#2e7d32",
                          fontWeight: 700,
                        }}
                      >
                        Available:{" "}
                        <strong>{formatCurrency(avail.availableBudgetCost)}</strong>
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <Button
          variant="outlined"
          onClick={onCreate}
          disabled={isCreateDisabled()}
        >
          {selectedLineItems.some(
            (l) => budgetAvailabilityMap[String(l._id ?? l.id ?? "")]?.checking
          )
            ? "Checking budget…"
            : "Create Requisition"}
        </Button>
      </div>
    </CustomModal>
  );
};

export default PRCreateModal;
